import React, { useState, useEffect, useRef } from 'react';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import NavigationBreadcrumb from '../../components/ui/NavigationBreadcrumb';
import NPSKPICards from './components/NPSKPICards';
import NPSTrendChart from './components/NPSTrendChart';
import NPSQuestionTable from './components/NPSQuestionTable';
import NPSWordCloud from './components/NPSWordCloud';
import Icon from '../../components/AppIcon';
import api from '../../api/axios';

const NPSAnalytics = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [headerKpi, setHeaderKpi] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const dateRangeRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 700);
    return () => clearTimeout(timer);
  }, []);

  const handleDownloadCsv = async () => {
    const range = dateRangeRef.current;
    if (!range) return;

    setDownloading(true);
    try {
      const response = await api.get('/api/v1/nps-analytics/export', {
        params: { from: range.from, to: range.to },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'nps_data.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download NPS data:', err);
    } finally {
      setDownloading(false);
    }
  };

  const sections = [
    { id: 'overview', label: 'Key Widgets & Overview', icon: 'LayoutDashboard' },
    { id: 'questions', label: 'Question-Level Details', icon: 'List' },
    { id: 'themes', label: 'Top Response Themes', icon: 'MessageCircle' },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <PrimaryNavigation />
        <NavigationBreadcrumb />
        <div className="flex items-center justify-center h-[calc(100vh-128px)]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Loading NPS analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PrimaryNavigation />
      <div className="main-content">
        <div className="min-h-screen bg-background">
          <NavigationBreadcrumb />
          <main className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">

            {/* Page Header */}
            <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1E3A8A, #0F766E)' }}>
                    <Icon name="Star" size={20} color="#FFFFFF" />
                  </div>
                  <h1 className="text-2xl md:text-3xl font-semibold text-foreground">NPS Analytics</h1>
                </div>
                <p className="text-sm md:text-base text-muted-foreground">
                  Net Promoter Score performance, question-level insights, and voice of customer themes
                </p>
              </div>

              {/* NPS Score Badge */}
              {headerKpi && (() => {
                const f2 = (v) => parseFloat(Number(v).toFixed(2));
                return (
                <div className="flex-shrink-0 flex items-center gap-3 bg-card border border-border rounded-xl px-5 py-3" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Current NPS</p>
                    <div className="flex items-center gap-2">
                      <span className="text-3xl font-bold data-text" style={{ color: headerKpi.npsScore >= 50 ? '#059669' : headerKpi.npsScore >= 0 ? '#D97706' : '#DC2626' }}>
                        {f2(headerKpi.npsScore)}
                      </span>
                      {headerKpi.previousNPS != null && (() => {
                        const change = f2(headerKpi.npsScore - headerKpi.previousNPS);
                        const isPositive = change >= 0;
                        return (
                          <div className="flex items-center gap-0.5">
                            <Icon name={isPositive ? 'ArrowUpRight' : 'ArrowDownRight'} size={14} color={isPositive ? '#059669' : '#DC2626'} />
                            <span className="text-xs font-medium" style={{ color: isPositive ? '#059669' : '#DC2626' }}>
                              {isPositive ? '+' : ''}{change} vs prev
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="w-px h-10 bg-border" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Response Rate</p>
                    <span className="text-2xl font-bold data-text" style={{ color: headerKpi.responseRate >= 50 ? '#059669' : headerKpi.responseRate >= 30 ? '#D97706' : '#DC2626' }}>
                      {f2(headerKpi.responseRate)}%
                    </span>
                  </div>
                </div>
                );
              })()}
            </div>

            {/* Section Navigation */}
            <div className="flex items-center gap-1 bg-muted rounded-xl p-1 mb-6 w-fit">
              {sections?.map((sec) => (
                <button
                  key={sec?.id}
                  onClick={() => setActiveSection(sec?.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeSection === sec?.id
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={sec?.icon} size={15} />
                  <span className="hidden sm:inline">{sec?.label}</span>
                  <span className="sm:hidden">{sec?.label?.split(' ')?.[0]}</span>
                </button>
              ))}
            </div>

            {/* Section 1: Key Widgets & Overview */}
            {activeSection === 'overview' && (
              <div>
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-5 rounded-full bg-primary" />
                    <h2 className="text-lg font-semibold text-foreground">Key Widgets & Overview</h2>
                    <span className="text-xs text-muted-foreground ml-1">— NPS performance at a glance</span>
                  </div>
                  <button
                    onClick={handleDownloadCsv}
                    disabled={downloading}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    <Icon name="Download" size={14} />
                    {downloading ? 'Downloading...' : 'Download CSV'}
                  </button>
                </div>
                <NPSKPICards onDataLoaded={setHeaderKpi} onDateRangeChange={(range) => { dateRangeRef.current = range; }} />
                <NPSTrendChart />
              </div>
            )}

            {/* Section 2: Question-Level Details */}
            {activeSection === 'questions' && (
              <div>
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-1 h-5 rounded-full bg-secondary" />
                  <h2 className="text-lg font-semibold text-foreground">Question-Level Details</h2>
                  <span className="text-xs text-muted-foreground ml-1">— Identify which questions drive dissatisfaction</span>
                </div>
                <NPSQuestionTable />
              </div>
            )}

            {/* Section 3: Top Response Themes */}
            {activeSection === 'themes' && (
              <div>
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-1 h-5 rounded-full bg-accent" />
                  <h2 className="text-lg font-semibold text-foreground">Top Response Themes</h2>
                  <span className="text-xs text-muted-foreground ml-1">— Voice of customer from unstructured data</span>
                </div>
                <NPSWordCloud />
              </div>
            )}
          </main>
        </div>
      </div>
    </>
  );
};

export default NPSAnalytics;
